// `start` and `end` are RFC 3339 strings. `allDay` marks whole-day events,
// which render without a time.
export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  location?: string;
  start: string;
  end: string;
  allDay: boolean;
  attendees?: string[];
  htmlLink?: string;
  status?: "confirmed" | "tentative" | "cancelled";
}

export interface CalendarEventsPayload {
  type: "calendar_events";
  events: CalendarEvent[];
  totalResults: number;
  timeMin?: string;
  timeMax?: string;
}

const isString = (v: unknown): v is string => typeof v === "string";
const isNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

const isCalendarEvent = (v: unknown): v is CalendarEvent => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return (
    isString(r.id) &&
    isString(r.title) &&
    (r.description === undefined || isString(r.description)) &&
    (r.location === undefined || isString(r.location)) &&
    isString(r.start) &&
    isString(r.end) &&
    typeof r.allDay === "boolean" &&
    (r.attendees === undefined || (Array.isArray(r.attendees) && r.attendees.every(isString))) &&
    (r.htmlLink === undefined || isString(r.htmlLink)) &&
    (r.status === undefined || r.status === "confirmed" || r.status === "tentative" || r.status === "cancelled")
  );
};

export const parseCalendarEventsPayload = (v: unknown): CalendarEventsPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "calendar_events") return null;
  if (!Array.isArray(r.events)) return null;
  if (!r.events.every(isCalendarEvent)) return null;
  if (!isNumber(r.totalResults)) return null;
  if (r.timeMin !== undefined && !isString(r.timeMin)) return null;
  if (r.timeMax !== undefined && !isString(r.timeMax)) return null;
  return {
    type: "calendar_events",
    events: r.events,
    totalResults: r.totalResults,
    timeMin: r.timeMin,
    timeMax: r.timeMax,
  };
};
